// Master keys remain at the authority origin. Messages contain public challenges only.
export const BROKER_ORIGIN = 'https://office-next.naezipscan.com';
export const SITE_ORIGINS = Object.freeze([
  BROKER_ORIGIN, 'https://office.naezipscan.com', 'https://watson.naezipscan.com',
  'https://wiki.kredatalab.com', 'https://xray.naezipscan.com',
]);
// v1: 발급한 로그인 한 번·최대 8시간. v2: 사이트·사람·이 브라우저 자식 키에 묶고 30일 — 새 로그인은 여전히
// OTP(또는 방금 한 OTP 로그인)로 연결해야 한다(KRE-17). 둘 다 받아들이고, v2 는 로그인과 무관한 칸에 둔다.
const VERSIONS = Object.freeze({ 'admin-device-site-v1': 8*3600000, 'admin-device-site-v2': 30*24*3600000 });
const PROTOCOL = 'admin-device-connect-v1';
const BOOTSTRAP = { _deviceBootstrap: true };
const enc = new TextEncoder();
const b64 = bytes => btoa(String.fromCharCode(...new Uint8Array(bytes)));
const fail = reason => new Error('device_shared_' + reason);
const random = () => b64(crypto.getRandomValues(new Uint8Array(32))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
function originAllowed(origin) { return SITE_ORIGINS.includes(origin); }
function authority(status) {
  if (status?.shared_enabled !== true || status.broker_origin !== BROKER_ORIGIN || !status.actor_user_no || !status.session_id || !status.api_audience) throw fail('unavailable');
  return status;
}
async function store(mode, action) {
  const db = await new Promise((resolve,reject) => {
    const r = indexedDB.open('naezipscan-shared-device-v1',1);
    r.onupgradeneeded=()=>r.result.createObjectStore('keys');
    r.onerror=()=>reject(fail('storage')); r.onsuccess=()=>resolve(r.result);
  });
  try { return await new Promise((resolve,reject)=>{
    const tx=db.transaction('keys',mode), r=action(tx.objectStore('keys'));
    tx.oncomplete=()=>resolve(r.result); tx.onerror=tx.onabort=()=>reject(fail('storage'));
  }); } finally { db.close(); }
}
const slot = (status, origin) => JSON.stringify([status.actor_user_no,status.session_id,origin]);
const siteSlot = (actor, origin) => JSON.stringify([actor,origin]);
const slotFor = (status, origin, text) => JSON.parse(text)[0]==='admin-device-site-v2' ? siteSlot(status.actor_user_no,origin) : slot(status,origin);
function liveSiteCertificate(cert){try{const c=JSON.parse(cert?.text);return Array.isArray(c)&&c[0]==='admin-device-site-v2'&&Number.isSafeInteger(c[10])&&c[10]>Date.now();}catch{return false;}}
export function rememberSharedStatus(status) {
  if (status.shared_enabled === true) {
    authority(status);
    sessionStorage.setItem('admin_shared_device_context', JSON.stringify({actor_user_no:status.actor_user_no,session_id:status.session_id}));
    localStorage.setItem('admin_device_shared_hint','true');
  } else { localStorage.removeItem('admin_device_shared_hint'); sessionStorage.removeItem('admin_shared_device_context'); }
}
export async function sharedLocalDevice() {
  if (localStorage.getItem('admin_device_shared_hint') !== 'true') return null;
  let context;
  try { context=JSON.parse(sessionStorage.getItem('admin_shared_device_context') || 'null'); } catch { return null; }
  if (!context?.actor_user_no || !context?.session_id) return null;
  const session = await store('readonly',s=>s.get(slot(context,window.location.origin)));
  if (session) return session;
  // 새 로그인: 같은 사람·같은 사이트의 v2 연결 키를 팝업 없이 다시 쓴다(연결은 OTP·로그인 인증으로 따로).
  const site = await store('readonly',s=>s.get(siteSlot(context.actor_user_no,window.location.origin)));
  return site && liveSiteCertificate(site.site_certificate) ? site : null;
}
export async function forgetSharedDevice() {
  let context;try{context=JSON.parse(sessionStorage.getItem('admin_shared_device_context') || 'null');}catch{context=null;}
  sessionStorage.removeItem('admin_shared_device_context');
  if(context?.actor_user_no && context?.session_id) await store('readwrite',s=>s.delete(slot(context,window.location.origin)));
}
export function inspectCertificate(text, expected) {
  if(typeof text!=='string' || enc.encode(text).length>4096)throw fail('certificate');
  let c;try{c=JSON.parse(text);}catch{throw fail('certificate');}
  if(!Array.isArray(c)||c.length!==11||!Object.prototype.hasOwnProperty.call(VERSIONS,c[0])||c.some(x=>!['string','number'].includes(typeof x)))throw fail('certificate');
  const [,, ,parent,origin,audience,publicKey,id,nonce,issued,expires] = c;
  if(!originAllowed(origin)||!Number.isSafeInteger(issued)||!Number.isSafeInteger(expires)||expires<=Date.now()||issued>Date.now()+30000||expires-issued>VERSIONS[c[0]]||issued>=expires)throw fail('certificate');
  for(const [index,key] of [[1,'actor_user_no'],[2,'session_id'],[3,'device_id'],[4,'origin'],[5,'api_audience'],[6,'public_key'],[7,'challenge_id'],[8,'nonce']]) {
    if(expected[key]!==undefined&&c[index]!==expected[key])throw fail('certificate_binding');
  }
  return {actor_user_no:c[1],session_id:c[2],device_id:parent,requester_origin:origin,api_audience:audience,public_key:publicKey,challenge_id:id,nonce,issued_at:issued,expires_at:expires};
}
function channelMessage(data,channel) {
  if(!data || Object.getPrototypeOf(data)!==Object.prototype)return false;
  const allowed={ready:['device_id'],challenge:['challenge_id','nonce','certificate_text'],signed:['certificate_text','signature'],error:[]};
  if(typeof data.type!=='string'||!Object.prototype.hasOwnProperty.call(allowed,data.type))return false;
  const keys=['protocol','channel','type',...allowed[data.type]];
  const own=Object.keys(data);if(own.length!==keys.length||own.some(k=>!keys.includes(k)))return false;
  const limits={protocol:40,channel:43,type:12,device_id:100,challenge_id:100,nonce:100,certificate_text:4096,signature:200};
  for(const key of keys)if(typeof data[key]!=='string'||data[key].length>limits[key])return false;
  return data.protocol===PROTOCOL&&data.channel===channel;
}
// Call directly from a user click: opening happens before any await (popup blockers).
export function connectSharedDevice(api,status,{signal,timeoutMs=120000,windowObject=window}={}) {
  authority(status);
  const origin=windowObject.location.origin;
  if(!originAllowed(origin)||!windowObject.isSecureContext)throw fail('origin');
  const channel=random();
  const url=new URL('/device-authority',BROKER_ORIGIN);
  url.hash=new URLSearchParams({channel,origin}).toString();
  const popup=windowObject.open(url.href,'_blank','popup,width=620,height=760');
  if(!popup)throw fail('popup_blocked');
  return new Promise((resolve,reject)=>{
    let done=false,stage='ready',pair,publicKey,challenge;const requestController=new AbortController();
    const finish=(error,result)=>{if(done)return;done=true;requestController.abort();clearTimeout(timer);clearInterval(closed);windowObject.removeEventListener('message',receive);signal?.removeEventListener('abort',abort);try{popup.close();}catch{}error?reject(error):resolve(result);};
    const abort=()=>finish(fail('cancelled'));
    const timer=setTimeout(()=>finish(fail('timeout')),Math.min(Math.max(timeoutMs,1000),120000));
    const closed=setInterval(()=>{if(popup.closed)finish(fail('popup_closed'));},250);
    async function receive(event){
      if(done||event.source!==popup||event.origin!==BROKER_ORIGIN||!channelMessage(event.data,channel))return;
      const data=event.data;
      try {
        if(data.type==='error')throw fail('authority_denied');
        if(stage==='ready'&&data.type==='ready'){
          stage='challenge';
          if(typeof data.device_id!=='string'||data.device_id.length>100)throw fail('device');
          pair=await crypto.subtle.generateKey({name:'ECDSA',namedCurve:'P-256'},false,['sign','verify']);
          publicKey=b64(await crypto.subtle.exportKey('spki',pair.publicKey));
          const response=await api.post('/admin-devices/site-challenge',{device_id:data.device_id,public_key:publicKey},{...BOOTSTRAP,signal:requestController.signal});
          if(done)return;
          challenge=response.data;
          inspectCertificate(challenge.certificate_text,{...status,device_id:data.device_id,origin,public_key:publicKey,challenge_id:challenge.challenge_id,nonce:challenge.nonce});
          stage='signature';
          popup.postMessage({protocol:PROTOCOL,channel,type:'challenge',challenge_id:challenge.challenge_id,nonce:challenge.nonce,certificate_text:challenge.certificate_text},BROKER_ORIGIN);
        }else if(stage==='signature'&&data.type==='signed'){
          stage='complete';
          if(data.certificate_text!==challenge.certificate_text||typeof data.signature!=='string'||data.signature.length>200)throw fail('response');
          const {data:result}=await api.post('/admin-devices/site-complete',{challenge_id:challenge.challenge_id,nonce:challenge.nonce,certificate_text:challenge.certificate_text,signature:data.signature},{...BOOTSTRAP,signal:requestController.signal});
          if(done)return;
          if(result.device_id!==JSON.parse(challenge.certificate_text)[3]||result.site_certificate?.text!==challenge.certificate_text||result.site_certificate?.signature!==data.signature)throw fail('response');
          const saved={device_id:result.device_id,privateKey:pair.privateKey,site_certificate:result.site_certificate};
          await store('readwrite',s=>s.put(saved,slotFor(status,origin,result.site_certificate.text)));
          rememberSharedStatus(status);finish(null,result);
        }
      }catch{finish(fail('connection_failed'));}
    }
    windowObject.addEventListener('message',receive);signal?.addEventListener('abort',abort,{once:true});if(signal?.aborted)abort();
  });
}
// Only the top-level common-origin page may invoke this. It signs a server-inspected,
// explicitly confirmed certificate, never arbitrary caller-provided request text.
export function startDeviceAuthority(api,status,master,{review,signal,timeoutMs=120000,windowObject=window}={}) {
  authority(status);
  if(windowObject.location.origin!==BROKER_ORIGIN||windowObject.top!==windowObject.self||!windowObject.opener||!master?.privateKey||master.actor_user_no!==status.actor_user_no||master.privateKey.extractable!==false||typeof review!=='function')throw fail('authority');
  const args=new URLSearchParams(windowObject.location.hash.slice(1)),channel=args.get('channel'),origin=args.get('origin');
  if(!channel||!/^[A-Za-z0-9_-]{43}$/.test(channel)||!originAllowed(origin))throw fail('origin');
  const opener=windowObject.opener;
  let done=false,busy=false;const requestController=new AbortController();
  const send=(type,extra={})=>opener.postMessage({protocol:PROTOCOL,channel,type,...extra},origin);
  const stop=()=>{if(done)return;done=true;requestController.abort();clearTimeout(timer);windowObject.removeEventListener('message',receive);signal?.removeEventListener('abort',cancel);};
  const cancel=()=>{if(done)return;send('error');stop();};
  const timer=setTimeout(cancel,Math.min(Math.max(timeoutMs,1000),120000));
  async function receive(event){
    if(done||busy||event.source!==opener||event.origin!==origin||!channelMessage(event.data,channel)||event.data.type!=='challenge')return;
    busy=true;
    try {
      const {challenge_id,nonce,certificate_text}=event.data;
      const details=inspectCertificate(certificate_text,{actor_user_no:status.actor_user_no,device_id:master.device_id,origin,api_audience:status.api_audience,challenge_id,nonce});
      const {data:inspected}=await api.post('/admin-devices/site-inspect',{challenge_id,nonce,certificate_text},{...BOOTSTRAP,signal:requestController.signal});
      if(done)return;
      if(inspected.verified!==true||inspected.device_id!==details.device_id||inspected.requester_origin!==details.requester_origin||inspected.actor_user_no!==details.actor_user_no||inspected.certificate_text!==certificate_text)throw fail('inspection');
      const child_key_fingerprint=Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256',Uint8Array.from(atob(details.public_key),c=>c.charCodeAt(0)))),x=>x.toString(16).padStart(2,'0')).join('');
      if(!await review({...details,key_fingerprint:master.key_fingerprint,child_key_fingerprint}))throw fail('consent');
      if(done||signal?.aborted)return;
      // Revalidate expiration after the user has read the consent screen.
      inspectCertificate(certificate_text,{actor_user_no:status.actor_user_no,device_id:master.device_id,origin,api_audience:status.api_audience,challenge_id,nonce});
      const signature=b64(await crypto.subtle.sign({name:'ECDSA',hash:'SHA-256'},master.privateKey,enc.encode(certificate_text)));
      if(!done)send('signed',{certificate_text,signature});stop();
    }catch{if(!done)send('error');stop();}
  }
  windowObject.addEventListener('message',receive);signal?.addEventListener('abort',cancel,{once:true});
  if(signal?.aborted)cancel();else send('ready',{device_id:master.device_id});
  return stop;
}
